import React, { Fragment, useEffect, useState } from 'react';
import { Button } from '@asu/components-core/dist/libCore.es';
import { useSelector } from 'react-redux';
import { Doughnut } from 'react-chartjs-2';
import {
  faCheck,
  faTriangleExclamation,
  faCircle,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TabPanel from './TabPanel';
import { classNames, createDoughnutData } from 'utils';
import { Modal } from 'react-bootstrap';
import CourseListForm from 'components/CourseListForm';
import ChecksheetHistory from './ChecksheetHistory';
import StaticData from './StaticData';
import useServiceAuth from 'auth/useServiceAuth';
import { v4 as uuidv4 } from 'uuid';
import { fetchDplUndergradMinorsCertificates } from '../apis/dplAPIs';
import {
  checksheetGet,
  checksheetGetAllByYear,
  checksheetGetAllProposedByYear,
} from '../apis/checksheetAPIs';
import SearchProgram from 'components/SearchProgram';
import SearchProposed from 'components/SearchProposed';

const proposedString = '(proposed) ';

const ChecksheetValidation = ({
  checksheetId,
  validation,
  getValidationIsPending,
  isLocked,
  holdsLock,
  showHistory,
  handleRefetchChecksheet,
  currentTab,
  setCurrentTab,
}) => {
  const totalHours = validation.totalHours;
  const totalUpperDivisionHours = validation.totalUpperDivisionHours;
  const totalGSHours = validation.totalGSHours;
  const totalGSUpperDivisionHours = validation.totalGSUpperDivisionHours;
  const hours = validation.validTotalHours;
  const maxHours = validation.validMaxHours;
  const upperDivisionHours = validation.validUpperDivisionHours;
  const gsHours = validation.validGSHours;
  const gsUpperDivisionHours = validation.validGSUpperDivisionHours;

  const [showCourseListModal, setShowCourseListModal] = useState();
  const [selectedCourseListId, setSelectedCourseListId] = useState();
  const [showModal, setShowModal] = useState(false);

  const courseLists = useSelector((state) => state.checksheet.courseLists);
  const details = useSelector((state) => state.checksheet.details);
  const collegeList = useSelector((state) => state.dataLists.collegeList);

  // === Course List Import Panel State ===
  // Track selected course list IDs for import
  const [importSelectedCourseListIds, setImportSelectedCourseListIds] =
    useState([]);
  // Show/hide modal for viewing a course list from import panel
  const [showImportViewModal, setShowImportViewModal] = useState(false);
  // Store the course list object to view in modal
  const [importViewCourseListObj, setImportViewCourseListObj] = useState(null);

  // Show/hide the import panel UI
  const [showImportPanel, setShowImportPanel] = useState(false);
  // Selected catalog year for import panel dropdown
  const [importCatalogYear, setImportCatalogYear] = useState('');

  // Store all checksheets and proposed checksheets for selected year
  const [checksheetList, setChecksheetList] = useState([]);
  const [proposedList, setProposedList] = useState([]);

  // Store all programs and loading state for import panel
  const [programList, setProgramList] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);

  // Get access token for API calls
  const { getAccessToken } = useServiceAuth();

  // Search/filter state for import panel
  const [importSearchString, setImportSearchString] = useState('');
  // Filtered regular programs for import panel
  const [importFilteredPrograms, setImportFilteredPrograms] = useState([]);
  // Filtered proposed programs for import panel
  const [filteredProposed, setFilteredProposed] = useState([]);

  // Selected program and checksheet for import panel
  const [importSelectedProgram, setImportSelectedProgram] = useState(null);
  const [importSelectedChecksheet, setImportSelectedChecksheet] =
    useState(null);
  // Loading state for course list fetch
  const [importChecksheetLoading, setImportChecksheetLoading] = useState(false);

  const historyRequirements = useSelector(
    (state) => state.history.requirements
  );

  const handleCloseModal = () => setShowModal(false);

  const handleEditCourseList = (key) => {
    // console.log('Editing Course List with key:', key);
    // dispatch(setCourseListId(key));
    // dispatch(setCourseListName(courseLists[key]?.courseListName || ''));
    // dispatch(setCourses(courseLists[key]?.courses || []));
    setSelectedCourseListId(key);
    setShowCourseListModal(true);
    setShowModal(true);
  };

  useEffect(() => {
    // to handle unsaved course list data. either it is a new course list or an existing one
    const unsavedData = sessionStorage.getItem('unsavedCourses');
    console.log('Unsaved data in validation tab:', unsavedData);
    if (unsavedData) {
      const newCourseListName = JSON.parse(unsavedData).courseListName;

      let foundCourseList = false;
      for (const courseListId in courseLists) {
        if (courseLists[courseListId].courseListName === newCourseListName) {
          foundCourseList = true;
          setSelectedCourseListId(courseListId);
        }
      }
      if (!foundCourseList) {
        handleEditCourseList(null);
      }
    }
  }, [courseLists]);

  // Fetch all programs for the import panel (for selected catalog year)
  const fetchAllPrograms = async () => {
    try {
      setLoadingPrograms(true);
      const programs = await fetchDplUndergradMinorsCertificates();
      setProgramList(programs || []);
    } catch (err) {
      setProgramList([]);
    } finally {
      setLoadingPrograms(false);
    }
  };

  // Fetch all regular checksheets for the selected catalog year (import panel)
  const fetchAllChecksheets = async (year) => {
    try {
      const token = await getAccessToken();
      const checksheets = await checksheetGetAllByYear({ year, token });
      setChecksheetList(checksheets || []);
    } catch (err) {
      setChecksheetList([]);
    }
  };

  // Fetch all proposed checksheets for the selected catalog year (import panel)
  const fetchAllProposedPrograms = async (year) => {
    try {
      const token = await getAccessToken();
      const checksheets = await checksheetGetAllProposedByYear({ year, token });
      setProposedList(checksheets || []);
    } catch (err) {
      setProposedList([]);
    }
  };

  // Load all programs, checksheets, and proposed checksheets for selected year (import panel)
  const loadProgramsForYear = async (year) => {
    await fetchAllPrograms();
    await fetchAllChecksheets(year);
    await fetchAllProposedPrograms(year);
  };

  // Filter regular programs for import panel based on search string and checksheet existence
  useEffect(() => {
    //loop checksheetList first, then programs
    let filteredProgramsSet = new Set();
    if (Array.isArray(checksheetList) && Array.isArray(programList)) {
      checksheetList.forEach((cs) => {
        const skStr = typeof cs.sk?.S === 'string' ? cs.sk.S : '';
        programList.forEach((program) => {
          // Exclude if catalog year and acadplan match current checksheet
          if (
            importCatalogYear &&
            details?.year &&
            details?.acadplan &&
            importCatalogYear === details.year &&
            program.acadPlanCode === details.acadplan
          ) {
            return;
          }
          const hasPlan = skStr.includes(program.acadPlanCode);
          let subplanMatch = true;
          let matchedSubPlanCode = undefined;
          let matchedSubPlanDescription = undefined;
          if (skStr.includes('null')) {
            subplanMatch = true;
          } else if (Array.isArray(program.subplans) && program.subplans.length > 0) {
            subplanMatch = false;
            for (const subplanObj of program.subplans) {
              if (subplanObj.acadSubPlanCode && skStr.includes(subplanObj.acadSubPlanCode)) {
                subplanMatch = true;
                matchedSubPlanCode = subplanObj.acadSubPlanCode;
                matchedSubPlanDescription = subplanObj.description;
                break;
              }
            }
          }
          if (hasPlan && subplanMatch) {
            // check search string match
            const q = importSearchString.toLowerCase();
            let matchesSearch = false;
            if (!q) matchesSearch = true;
            else if (program.acadPlanCode?.toLowerCase().includes(q)) matchesSearch = true;
            else if (program.acadPlanDescription?.toLowerCase().includes(q)) matchesSearch = true;
            else if (Array.isArray(program.subplans)) {
              if (
                program.subplans.some((subplanObj) =>
                  subplanObj.acadSubPlanCode?.toLowerCase().includes(q)
                )
              ) matchesSearch = true;
              else if (
                program.subplans.some((subplanObj) =>
                  subplanObj.description?.toLowerCase().includes(q)
                )
              ) matchesSearch = true;
            }
            if (matchesSearch) {
              const programWithSubplan = matchedSubPlanCode
                ? { ...program, acadSubPlanCode: matchedSubPlanCode, acadSubPlanDescription: matchedSubPlanDescription }
                : program;
              filteredProgramsSet.add(programWithSubplan);
            }
          }
        });
      });
    }
    const filtered = Array.from(filteredProgramsSet);
    setImportFilteredPrograms(filtered);
  }, [
    importSearchString,
    programList,
    checksheetList,
    details,
    importCatalogYear,
  ]);

  // Filter proposed programs for import panel based on search string
  useEffect(() => {
    const filtered = proposedList.filter(
      (proposed) =>
        (proposed.acadplan || '')
          .toLowerCase()
          .includes(importSearchString.toLowerCase()) ||
        (proposedString + (proposed.checksheetName || ''))
          .toLowerCase()
          .includes(importSearchString.toLowerCase())
    );
    setFilteredProposed(filtered);
  }, [proposedList, importSearchString]);

  // Handle selection of a program or proposed program from the import panel
  // Sets selected program and fetches its checksheet details
  const handleSelectChecksheet = async (program, proposed = false) => {
    setImportSelectedProgram(program);
    setImportSearchString('');
    setImportChecksheetLoading(true);

    // Find the matching checksheet in checksheetList by sk
    let checksheet = null;
    if (proposed === false) {
      if (Array.isArray(checksheetList)) {
        checksheet = checksheetList.find((cs) => {
          const skStr = typeof cs.sk?.S === 'string' ? cs.sk.S : '';
          const hasPlan = skStr.includes(program.acadPlanCode);
          const hasSubplan =
            program.acadSubPlanCode
              ? skStr.includes(program.acadSubPlanCode)
              : true;
          return hasPlan && hasSubplan;
        });
      }
    } else {
      // For proposed, set custom program object
      checksheet = program;
      setImportSelectedProgram({
        acadplan: program.acadplan,
        checksheetName: program.checksheetName,
        college: program.college,
        proposed: true,
      });
    }
    // Fetch the full checksheet by id (sk) and set as selectedChecksheet
    if (checksheet && checksheet.sk?.S) {
      try {
        const token = await getAccessToken();
        const fullChecksheet = await checksheetGet({
          id: checksheet.sk.S,
          token,
        });
        setImportSelectedChecksheet(fullChecksheet || null);
      } catch (err) {
        setImportSelectedChecksheet(null);
      } finally {
        setImportChecksheetLoading(false);
      }
    } else {
      setImportSelectedChecksheet(null);
      setImportChecksheetLoading(false);
    }
  };

  return (
    <>
      {checksheetId && (
        <div
          className="sidebar-menu position-fixed d-flex flex-column border-end border-bottom border-gray-4 bg-gray-1"
          style={{ width: '20%', maxHeight: '100vh', overflowY: 'auto' }}
        >
          <TabPanel
            tabs={['Rules', 'Course Lists', 'History']}
            // icons={[faClipboardCheck, faList]}
            showExtraBorder={false}
            initialTab={showHistory ? 2 : 0}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          >
            <div className="d-flex flex-column gap-1 mt-1 overflow-y-hidden">
              <h4 className="px-3">Validation Rules</h4>

              <div className="scrollbox">
                <div className="scrollbox-content">
                  {getValidationIsPending ? (
                    <div className="px-3">Loading...</div>
                  ) : (
                    <div>
                      <div
                        className={classNames(
                          !!maxHours &&
                            maxHours >= hours &&
                            totalHours > maxHours &&
                            totalHours > hours &&
                            'bg-danger-custom',
                          'd-flex gap-1 px-3 py-1'
                        )}
                      >
                        {!hours && !maxHours ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-success"
                            style={{ width: '24px', height: '24px' }}
                          />
                        ) : (
                          <div style={{ paddingTop: '2px' }}>
                            <Doughnut
                              data={createDoughnutData(
                                totalHours,
                                !!maxHours && maxHours > hours
                                  ? maxHours
                                  : !!hours
                                    ? hours
                                    : 0,
                                !!maxHours &&
                                  maxHours >= hours &&
                                  totalHours > maxHours &&
                                  totalHours > hours
                              )}
                              style={{ width: '24px' }}
                            />
                          </div>
                        )}
                        <div>
                          <div className="fw-bold">
                            <span>Total Credit Hours</span>
                            {!!hours &&
                              !!maxHours &&
                              maxHours > hours &&
                              hours !== maxHours && (
                                <span> ({hours} minimum)</span>
                              )}
                          </div>
                          <div>
                            {!hours && !maxHours ? (
                              <span>
                                {totalHours ?? 0} (No minimum or maximum)
                              </span>
                            ) : (
                              <span>
                                {totalHours ?? 0}
                                <span className="text-gray-5"> / </span>
                                {maxHours > hours ? maxHours : hours}
                                {hours !== maxHours &&
                                  (!!maxHours && maxHours > hours ? (
                                    <span> maximum</span>
                                  ) : (
                                    !!hours &&
                                    hours > maxHours && <span> minimum</span>
                                  ))}
                                {!!hours && !maxHours && totalHours > hours && (
                                  <span className="text-success-custom bg-success-custom mx-1 px-1">
                                    Exceeds
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          {!!maxHours &&
                            maxHours >= hours &&
                            totalHours > maxHours &&
                            totalHours > hours && (
                              <div className="d-flex gap-1 align-items-center text-danger-custom">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                {maxHours > hours ? (
                                  <span>
                                    Cannot exceed {maxHours} credit hours
                                  </span>
                                ) : (
                                  maxHours === hours && (
                                    <span>
                                      Must equal {maxHours} credit hours
                                    </span>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      </div>

                      <div className="d-flex gap-1 px-3 py-1">
                        {!upperDivisionHours ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-success"
                            style={{ width: '24px', height: '24px' }}
                          />
                        ) : (
                          <div style={{ paddingTop: '2px' }}>
                            <Doughnut
                              data={createDoughnutData(
                                totalUpperDivisionHours,
                                upperDivisionHours ?? 0
                              )}
                              style={{ width: '24px' }}
                            />
                          </div>
                        )}
                        <div>
                          <div className="fw-bold">
                            <span>Total Upper Division Credit Hours</span>
                          </div>
                          <div>
                            {!upperDivisionHours ? (
                              <span>
                                {totalUpperDivisionHours ?? 0} (No minimum)
                              </span>
                            ) : (
                              <span>
                                {totalUpperDivisionHours ?? 0}
                                <span className="text-gray-5"> / </span>
                                {upperDivisionHours} minimum
                              </span>
                            )}
                            {!!upperDivisionHours &&
                              totalUpperDivisionHours > upperDivisionHours && (
                                <span className="text-success-custom bg-success-custom mx-1 px-1">
                                  Exceeds
                                </span>
                              )}
                          </div>
                        </div>
                      </div>

                      {totalGSHours &&
                        gsHours &&
                        StaticData.gsList
                          .toSorted((a, b) => a.value.localeCompare(b.value))
                          .map((gs, index) => (
                            <Fragment key={gs.value}>
                              <>
                                {!!gsHours?.[gs.value] &&
                                  gsHours[gs.value] >
                                    gsUpperDivisionHours[gs.value] && (
                                    <div className="d-flex gap-1 px-3 py-1">
                                      {!gsHours[gs.value] ? (
                                        <FontAwesomeIcon
                                          icon={faCheck}
                                          className="text-success"
                                          style={{
                                            width: '24px',
                                            height: '24px',
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            paddingTop: '2px',
                                          }}
                                        >
                                          <Doughnut
                                            data={createDoughnutData(
                                              totalGSHours[gs.value],
                                              gsHours[gs.value] ?? 0
                                            )}
                                            style={{ width: '24px' }}
                                          />
                                        </div>
                                      )}
                                      <div>
                                        <div className="fw-bold">
                                          <span>
                                            {gs.label} ({gs.value})
                                          </span>
                                        </div>
                                        <div>
                                          {!gsHours[gs.value] ? (
                                            <span>
                                              {totalGSHours[gs.value] ?? 0}{' '}
                                              credit hours (No minimum)
                                            </span>
                                          ) : (
                                            <span>
                                              {totalGSHours[gs.value] ?? 0}
                                              <span className="text-gray-5">
                                                {' '}
                                                /{' '}
                                              </span>
                                              {gsHours[gs.value]} minimum credit
                                              hours
                                            </span>
                                          )}
                                          {!!gsHours[gs.value] &&
                                            totalGSHours[gs.value] >
                                              gsHours[gs.value] && (
                                              <span className="text-success-custom bg-success-custom mx-1 px-1">
                                                Exceeds
                                              </span>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                {!!gsUpperDivisionHours?.[gs.value] &&
                                  gsUpperDivisionHours[gs.value] !== 0 && (
                                    <div className="d-flex gap-1 px-3 py-1">
                                      {!gsUpperDivisionHours[gs.value] ? (
                                        <FontAwesomeIcon
                                          icon={faCheck}
                                          className="text-success"
                                          style={{
                                            width: '24px',
                                            height: '24px',
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            paddingTop: '2px',
                                          }}
                                        >
                                          <Doughnut
                                            data={createDoughnutData(
                                              totalGSUpperDivisionHours[
                                                gs.value
                                              ],
                                              gsUpperDivisionHours[gs.value] ??
                                                0
                                            )}
                                            style={{ width: '24px' }}
                                          />
                                        </div>
                                      )}
                                      <div>
                                        <div className="fw-bold">
                                          <span>
                                            {gs.label} ({gs.value}) - Upper
                                            Division
                                          </span>
                                        </div>
                                        <div>
                                          {!gsUpperDivisionHours[gs.value] ? (
                                            <span>
                                              {totalGSUpperDivisionHours[
                                                gs.value
                                              ] ?? 0}{' '}
                                              credit hours (No minimum)
                                            </span>
                                          ) : (
                                            <span>
                                              {totalGSUpperDivisionHours[
                                                gs.value
                                              ] ?? 0}
                                              <span className="text-gray-5">
                                                {' '}
                                                /{' '}
                                              </span>
                                              {
                                                gsUpperDivisionHours[gs.value]
                                              }{' '}
                                              minimum credit hours
                                            </span>
                                          )}
                                          {!!gsUpperDivisionHours[gs.value] &&
                                            totalGSUpperDivisionHours[
                                              gs.value
                                            ] >
                                              gsUpperDivisionHours[
                                                gs.value
                                              ] && (
                                              <span className="text-success-custom bg-success-custom mx-1 px-1">
                                                Exceeds
                                              </span>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                              </>
                            </Fragment>
                          ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-1">
              <div className="gap-1 align-items-center px-3">
                <h4>Manage Course Lists</h4>
                {isLocked && holdsLock && (
                  <div className="d-flex gap-1">
                    <Button
                      label="Create list"
                      size="small"
                      color="maroon"
                      icon={['fa', 'plus']}
                      style={{ color: 'white' }}
                      onClick={() => handleEditCourseList(null)}
                    />
                    <Button
                      label="Import existing"
                      size="small"
                      color="maroon"
                      icon={['fa', 'plus']}
                      style={{ color: 'white' }}
                      onClick={() => {
                        const next = !showImportPanel;
                        setShowImportPanel(next);
                        if (!showImportPanel) {
                          // opening: load programs for currently selected year
                          loadProgramsForYear(importCatalogYear);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              {courseLists &&
                Object.keys(courseLists).map((id) => (
                  <div key={id} className="d-flex ps-4 pt-1">
                    <div
                      className="px-1 underline-maroon"
                      onClick={() => handleEditCourseList(id)}
                      role="button"
                    >
                      {historyRequirements && historyRequirements[id] && (
                        <FontAwesomeIcon
                          icon={faCircle}
                          className="text-teal underline-hover"
                          title="See changes"
                          style={{
                            width: '10px',
                            paddingTop: '4px',
                            paddingRight: '6px',
                          }}
                        />
                      )}
                      {courseLists[id]?.courseListName ||
                        'Untitled course list'}
                    </div>
                  </div>
                ))}

              {/* === NEW: Inline import panel at bottom (keeps original layout) === */}
              {showImportPanel && (
                <div
                  className="border-top mt-auto p-3 bg-white"
                  style={{ width: '100%' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fw-bold">Import existing checksheet</div>
                    <div>
                      <Button
                        label="Close"
                        size="small"
                        color="dark"
                        onClick={() => setShowImportPanel(false)}
                      />
                    </div>
                  </div>

                  {/* Show selected program SK if available */}

                  <div className="mb-2">
                    <div className="d-flex align-items-center mb-2">
                      <div
                        style={{
                          width: '4px',
                          height: '24px',
                          background: '#FFD700',
                          borderRadius: '2px',
                          marginRight: '10px',
                        }}
                      ></div>
                      <span className="fw-bold">Find a checksheet</span>
                    </div>
                    <label
                      htmlFor="catalog-year-select"
                      className="fw-bold mb-1"
                    >
                      Catalog year
                    </label>
                    <select
                      id="catalog-year-select"
                      aria-label="Catalog year"
                      value={importCatalogYear}
                      onChange={(e) => {
                        const y = e.target.value;
                        setImportCatalogYear(y);
                        loadProgramsForYear(y);
                        setImportSelectedProgram(null);
                        setImportSelectedChecksheet(null);
                        setImportSearchString('');
                      }}
                      className="form-select"
                      style={{
                        color: !importCatalogYear ? '#6c757d' : 'inherit',
                      }}
                    >
                      <option value="" disabled style={{ color: '#6c757d' }}>
                        Select catalog year
                      </option>
                      {StaticData.yearList.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    {loadingPrograms ? (
                      <div>Loading programs...</div>
                    ) : (
                      <>
                        <label
                          htmlFor="search-programs-input"
                          className="fw-bold mb-1"
                        >
                          Search programs
                        </label>
                        <div className="position-relative">
                          {!importSelectedProgram ? (
                            <>
                              <input
                                id="search-programs-input"
                                type="text"
                                placeholder="Search by program title or plan code"
                                value={importSearchString}
                                onChange={(e) =>
                                  setImportSearchString(e.target.value)
                                }
                                className="border border-gray-5 ps-2 pe-6 py-1 w-100"
                                aria-label="Search programs by title or plan code"
                                autoComplete="off"
                              />
                              {importSearchString && (
                                <div
                                  className="position-absolute border border-top-0 border-gray-5 w-100 overflow-y-auto"
                                  style={{ maxHeight: '198px', zIndex: '1' }}
                                >
                                  {filteredProposed.map(
                                    (proposed, index, array) => (
                                      <div
                                        key={index}
                                        className="bg-white px-2"
                                      >
                                        <div
                                          className="border-bottom border-gray-5 py-2"
                                          onClick={() =>
                                            handleSelectChecksheet(
                                              proposed,
                                              true
                                            )
                                          }
                                          role="button"
                                        >
                                          <SearchProposed
                                            proposed={proposed}
                                            college={
                                              proposed.college !== 'OTHER'
                                                ? collegeList[proposed.college]
                                                : { description: 'Other' }
                                            }
                                          />
                                        </div>
                                      </div>
                                    )
                                  )}
                                  {importFilteredPrograms.map(
                                    (program, index, array) => (
                                      <div
                                        key={program.acadPlanCode + index}
                                        className={classNames(
                                          index !== array.length - 1 &&
                                            'border-bottom',
                                          'border-gray-5 p-2 bg-white'
                                        )}
                                        onClick={() =>
                                          handleSelectChecksheet(program, false)
                                        }
                                        role="button"
                                      >
                                        <SearchProgram program={program} />
                                      </div>
                                    )
                                  )}
                                  {importFilteredPrograms.length === 0 && (
                                    <div className="p-2 fst-italic">
                                      No programs found
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="border border-primary rounded p-2 mt-2 bg-light position-relative">
                              <button
                                type="button"
                                className="btn btn-link p-0 text-danger d-flex align-items-center justify-content-center position-absolute import-clear-btn"
                                aria-label="Clear selected program"
                                onClick={() => {
                                  setImportSelectedProgram(null);
                                  setImportSelectedChecksheet(null);
                                }}
                              >
                                <span className="import-clear-x">×</span>
                              </button>
                              <div>
                                {importSelectedProgram?.proposed ? (
                                  <SearchProposed
                                    proposed={importSelectedProgram}
                                    college={
                                      importSelectedProgram.college !== 'OTHER'
                                        ? collegeList[
                                            importSelectedProgram.college
                                          ]
                                        : { description: 'Other' }
                                    }
                                  />
                                ) : (
                                  <SearchProgram
                                    program={importSelectedProgram}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Display sk and course list for selected checksheet */}
                        {importChecksheetLoading ? (
                          <div className="mt-3 fst-italic text-gray-5">
                            Loading course list...
                          </div>
                        ) : importSelectedChecksheet ? (
                          <div className="mt-3">
                            {/* Parse details as JSON and display courseLists */}
                            {(() => {
                              let importSelectedJsonDetails = null;
                              try {
                                importSelectedJsonDetails =
                                  typeof importSelectedChecksheet.details ===
                                  'string'
                                    ? JSON.parse(
                                        importSelectedChecksheet.details
                                      )
                                    : importSelectedChecksheet.details;
                              } catch (e) {
                                /* empty */
                              }
                              if (
                                importSelectedJsonDetails &&
                                importSelectedJsonDetails.courseLists &&
                                Object.keys(
                                  importSelectedJsonDetails.courseLists
                                ).length > 0
                              ) {
                                return (
                                  <>
                                    <div className="d-flex align-items-center mb-2">
                                      <div
                                        style={{
                                          width: '4px',
                                          height: '24px',
                                          background: '#FFD700',
                                          borderRadius: '2px',
                                          marginRight: '10px',
                                        }}
                                      ></div>
                                      <span className="fw-bold">
                                        Select lists
                                      </span>
                                    </div>
                                    <div className="mb-2">
                                      <div
                                        className="d-flex align-items-center mb-2"
                                        style={{ gap: '16px' }}
                                      >
                                        <button
                                          type="button"
                                          className="bg-transparent border-0 p-0 button-link font-small"
                                          onClick={() => {
                                            setImportSelectedCourseListIds(
                                              Object.keys(
                                                importSelectedJsonDetails.courseLists
                                              )
                                            );
                                          }}
                                        >
                                          Select All
                                        </button>
                                        <button
                                          type="button"
                                          className="bg-transparent border-0 p-0 button-link font-small"
                                          onClick={() => {
                                            setImportSelectedCourseListIds([]);
                                          }}
                                        >
                                          Clear All
                                        </button>
                                      </div>
                                      {Object.entries(
                                        importSelectedJsonDetails.courseLists
                                      ).map(([listId, listObj]) => (
                                        <div
                                          key={listId}
                                          className="mb-2 d-flex align-items-center justify-content-between"
                                          style={{ gap: '8px' }}
                                        >
                                          <label
                                            className="d-inline-flex align-items-center"
                                            style={{
                                              fontWeight: 'normal',
                                              color: 'inherit',
                                              marginBottom: 0,
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              name="courseListCheckbox"
                                              value={listId}
                                              checked={importSelectedCourseListIds.includes(
                                                listId
                                              )}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setImportSelectedCourseListIds(
                                                    (prev) => [...prev, listId]
                                                  );
                                                } else {
                                                  setImportSelectedCourseListIds(
                                                    (prev) =>
                                                      prev.filter(
                                                        (id) => id !== listId
                                                      )
                                                  );
                                                }
                                              }}
                                              style={{ marginRight: '8px' }}
                                            />
                                            <span
                                              style={{
                                                fontWeight: 'normal',
                                                color: 'inherit',
                                              }}
                                            >
                                              {listObj.courseListName || listId}
                                            </span>
                                          </label>
                                          <button
                                            type="button"
                                            className="bg-transparent border-0 p-0 button-link font-small"
                                            onClick={() => {
                                              setImportViewCourseListObj(
                                                listObj
                                              );
                                              setShowImportViewModal(true);
                                            }}
                                          >
                                            View
                                          </button>
                                        </div>
                                      ))}
                                      <Button
                                        label="Import lists"
                                        color="maroon"
                                        size="small"
                                        onClick={async () => {
                                          let importSelectedJsonDetails = null;
                                          try {
                                            importSelectedJsonDetails =
                                              typeof importSelectedChecksheet.details ===
                                              'string'
                                                ? JSON.parse(
                                                    importSelectedChecksheet.details
                                                  )
                                                : importSelectedChecksheet.details;
                                          } catch (e) {
                                            /* empty */
                                          }
                                          if (
                                            !importSelectedJsonDetails ||
                                            !importSelectedJsonDetails.courseLists
                                          ) {
                                            alert(
                                              'No course lists found in checksheet details.'
                                            );
                                            return;
                                          }
                                          // Build newCourseLists object with only selected lists
                                          const allSelectedCourseLists =
                                            importSelectedJsonDetails.courseLists;
                                          const newCourseLists = {};
                                          importSelectedCourseListIds.forEach(
                                            (id) => {
                                              if (allSelectedCourseLists[id]) {
                                                newCourseLists[uuidv4()] =
                                                  allSelectedCourseLists[id];
                                              }
                                            }
                                          );

                                          if (
                                            Object.keys(newCourseLists).length >
                                            0
                                          ) {
                                            // Merge current courseLists and newCourseLists
                                            const mergedCourseLists = {
                                              ...(details.courseLists || {}),
                                              ...newCourseLists,
                                            };
                                            // Build updated checksheet details
                                            const updatedDetails = {
                                              ...details,
                                              courseLists: mergedCourseLists,
                                            };
                                            // Call checksheetUpdate API
                                            try {
                                              const token =
                                                await getAccessToken();
                                              const { checksheetUpdate } =
                                                await import(
                                                  '../apis/checksheetAPIs'
                                                );
                                              await checksheetUpdate({
                                                id: checksheetId,
                                                jsonData: updatedDetails,
                                                token,
                                              });
                                              // Refresh courseLists after import
                                              if (
                                                typeof handleRefetchChecksheet ===
                                                'function'
                                              ) {
                                                handleRefetchChecksheet();
                                                setShowImportPanel(false);
                                              }
                                              alert(
                                                'Selected course list imported successfully!'
                                              );
                                            } catch (err) {
                                              alert(
                                                'Failed to send course list JSON.'
                                              );
                                            }
                                          }
                                          setImportSelectedProgram(null);
                                          setImportSelectedChecksheet(null);
                                          setImportSearchString('');
                                          setImportCatalogYear('');
                                        }}
                                        style={{ marginTop: '12px' }}
                                      />
                                    </div>
                                  </>
                                );
                              } else {
                                return (
                                  <div className="mb-2 fst-italic text-gray-5">
                                    No course lists found for the selected
                                    program.
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        ) : (
                          importSelectedProgram && (
                            <div className="mt-3">
                              <div className="fw-bold">Course List</div>
                              <div className="fst-italic text-gray-5">
                                No checksheet found for this program and year.
                              </div>
                            </div>
                          )
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {/* =========================================================== */}
              <ChecksheetHistory checksheetId={checksheetId} tab="courseList" />
            </div>

            <ChecksheetHistory
              checksheetId={checksheetId}
              handleRefetchChecksheet={handleRefetchChecksheet}
              tab="history"
            />
          </TabPanel>
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} backdrop="static">
        {showCourseListModal ? (
          <CourseListForm
            courseListId={selectedCourseListId}
            handleClose={handleCloseModal}
            isLocked={isLocked}
            holdsLock={holdsLock}
          />
        ) : (
          <div></div>
        )}
      </Modal>

      {/* Custom modal for import panel 'View' button using CourseListView */}
      <Modal
        show={showImportViewModal}
        onHide={() => setShowImportViewModal(false)}
        backdrop="static"
      >
        <CourseListView
          courseListObj={importViewCourseListObj}
          handleClose={() => setShowImportViewModal(false)}
        />
      </Modal>
    </>
  );
};

const CourseListView = ({ courseListObj, handleClose }) => {
  if (!courseListObj) {
    return (
      <div className="p-4">
        <div>No course list details available.</div>
        <div className="mt-3 text-end">
          <Button label="Close" color="gray" onClick={handleClose} />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '1224px',
        minWidth: 400,
        border: '2px solid #d1d5db',
        borderRadius: '8px',
      }}
    >
      <div className="px-6 pb-3 pt-6">
        <div className="fs-3 fw-bold mb-4">
          {courseListObj.courseListName || '(Untitled course list)'}
        </div>

        <div className="px-6 py-3 rounded" style={{ minHeight: 120 }}>
          {courseListObj.courses && courseListObj.courses.length > 0 ? (
            <>
              <div className="mb-2 fs-4 fw-bold">Items</div>
              <div className="w-100 bg-white rounded">
                <div
                  className="d-flex fw-bold pb-2"
                  style={{ borderBottom: '2px solid ' }}
                >
                  <div className="ps-0" style={{ width: '90%' }}>
                    List Item
                  </div>
                  <div className="ps-0">Credit Hours</div>
                </div>
                {courseListObj.courses.map((item, idx) => {
                  const getCreditHours = (obj) => {
                    if (obj.hours) {
                      if (obj.maxHours && obj.maxHours !== obj.hours) {
                        return `${obj.hours} - ${obj.maxHours}`;
                      }
                      return `${obj.hours}`;
                    }
                    return '';
                  };
                  let groupContent = null;
                  let creditContent = null;
                  if (
                    item.itemType === 'req_multiple' &&
                    Array.isArray(item.courses)
                  ) {
                    groupContent = (
                      <div className="row py-2 mx-0 border-gray-2">
                        {item.courses.map((course, cidx) => (
                          <React.Fragment key={cidx}>
                            {course.connector && (
                              <div
                                className={`my-1 fw-bold rounded-1 text-nowrap ${course.connector === 'or' ? 'or' : 'AND'}`}
                              >
                                {course.connector}
                              </div>
                            )}
                            <span>
                              {course.subject && (
                                <span className="fw-bold">
                                  {course.subject}{' '}
                                </span>
                              )}
                              {course.number && (
                                <span className="fw-bold">{course.number}</span>
                              )}
                              {course.topic?.description ? (
                                <span className="ms-1">
                                  {course.topic.description}
                                </span>
                              ) : course.proposedTitle ? (
                                <span className="ms-1">
                                  {course.proposedTitle}
                                </span>
                              ) : course.description ? (
                                <span className="ms-1">
                                  {course.description}
                                </span>
                              ) : null}
                              {course.gs && (
                                <span className="ms-1">({course.gs})</span>
                              )}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    );
                    creditContent = (
                      <div>
                        {item.courses.map((course, cidx) => (
                          <div key={cidx}>{getCreditHours(course)}</div>
                        ))}
                      </div>
                    );
                  } else if (item.itemType === 'req_course') {
                    groupContent = (
                      <span>
                        <span className="fw-bold text-maroon">Course:</span>{' '}
                        <span className="fw-bold">
                          {item.subject} {item.number}
                        </span>
                        {item.topic?.description ? (
                          <span className="ms-1">{item.topic.description}</span>
                        ) : item.proposedTitle ? (
                          <span className="ms-1">{item.proposedTitle}</span>
                        ) : item.description ? (
                          <span className="ms-1">{item.description}</span>
                        ) : null}
                        {item.gs && <span className="ms-1">({item.gs})</span>}
                      </span>
                    );
                    creditContent = <span>{getCreditHours(item)}</span>;
                  } else if (item.itemType === 'req_elective') {
                    groupContent = (
                      <span>
                        <span className="fw-bold text-maroon">Elective:</span>{' '}
                        <span className="fw-bold">
                          {item.subject ? item.subject + ' ' : ''}
                          {item.division ? item.division + ' ' : ''}Elective
                        </span>
                      </span>
                    );
                    creditContent = <span>{getCreditHours(item)}</span>;
                  } else if (item.itemType === 'req_gs') {
                    groupContent = (
                      <span>
                        <span className="fw-bold text-maroon">
                          General Studies:
                        </span>{' '}
                        <span className="fw-bold">
                          {item.division ? item.division + ' ' : ''}
                          {item.gs}
                        </span>
                      </span>
                    );
                    creditContent = <span>{getCreditHours(item)}</span>;
                  } else if (item.itemType === 'req_text') {
                    groupContent = (
                      <span>
                        <span className="fw-bold text-maroon">Text:</span>{' '}
                        <span className="fw-bold">
                          {item.group ? item.group + ': ' : ''}
                          {item.text || item.customText}
                        </span>
                      </span>
                    );
                    creditContent = <span>{getCreditHours(item)}</span>;
                  } else if (item.itemType === 'req_check') {
                    groupContent = (
                      <span>
                        <span className="fw-bold text-maroon">Check:</span>{' '}
                        <span className="fw-bold">
                          {item.checkType === 'gpa'
                            ? `Minimum ${item.gpa} GPA`
                            : ''}
                          {item.checkSubtype === 'asu_cum_gpa'
                            ? ' ASU Cumulative GPA'
                            : ''}
                          {item.checkSubtype === 'major_gpa'
                            ? ' Major GPA'
                            : ''}
                          {item.checkSubtype === 'subject'
                            ? ` GPA in all ${item.subject} ${item.division ? item.division + ' ' : ''}courses`
                            : ''}
                          {item.checkSubtype === 'courses' &&
                          Array.isArray(item.checkCourses) ? (
                            <span>
                              {' '}
                              GPA in{' '}
                              {item.checkCourses.map(
                                (c, i, arr) =>
                                  `${c.subject} ${c.number}${i < arr.length - 1 ? ', ' : ''}`
                              )}
                            </span>
                          ) : (
                            ''
                          )}
                          {item.milestone
                            ? ` Milestone: ${Object.values(item.milestone)[0]}`
                            : ''}
                        </span>
                      </span>
                    );
                    creditContent = <span>{getCreditHours(item)}</span>;
                  } else {
                    groupContent = (
                      <span>
                        <span className="fw-bold text-maroon">Other:</span>{' '}
                        <span className="fw-bold">{JSON.stringify(item)}</span>
                      </span>
                    );
                    creditContent = <span>{getCreditHours(item)}</span>;
                  }
                  return (
                    <div
                      key={idx}
                      className="d-flex align-items-center"
                      style={{
                        borderBottom: '1px solid #d1d5db',
                        minHeight: 48,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <FontAwesomeIcon icon={faLock} />
                        {groupContent}
                      </div>
                      <div
                        style={{
                          width: '140px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                        }}
                      >
                        {creditContent}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-muted">No courses in this list.</div>
          )}
        </div>
      </div>
      <div className="px-6 py-3 bg-gray-1">
        <div className="d-flex justify-content-start align-items-center">
          <Button onClick={handleClose} label="Cancel" color="gray" />
        </div>
      </div>
    </div>
  );
};

export default ChecksheetValidation;
